// useWantAdMlsMatches.js
// Infinite-scroll ("Show More") MLS-listing matches for a Want-Listing's
// Matches tab. The /v2/matches response nests the MLS section under
// `data.mlsMatches` (no per-section hasNextPage), so we derive hasNextPage
// from totalCount vs the rows loaded so far.
import { useInfiniteQuery } from "@tanstack/react-query";
import { getMatches } from "@/services/v2/matches";

const PAGE_LIMIT = 20;

// Want-ad ↔ MLS-listing "perfect fit" cutoff. Must match the perfectFits
// score in Want_ad_Join.model.js so the table pill and this tab agree.
const MLS_MATCH_MIN_SCORE = 50;

const useWantAdMlsMatches = ({ wantAdId, sortType, enabled = true }) => {
  const query = useInfiniteQuery({
    queryKey: ["wantAdMlsMatches", wantAdId, sortType],
    enabled: !!wantAdId && enabled,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getMatches({
        wantAdId,
        page: pageParam,
        limit: PAGE_LIMIT,
        sortType,
        minScore: MLS_MATCH_MIN_SCORE,
      });
      return response?.data?.mlsMatches || { data: [], totalCount: 0 };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (sum, page) => sum + (page?.data?.length || 0),
        0,
      );
      return loaded < (lastPage?.totalCount || 0) ? allPages.length + 1 : undefined;
    },
    select: (result) => ({
      mlsMatches: result.pages.flatMap((page) => page?.data || []),
      totalMlsMatches: result.pages[0]?.totalCount || 0,
    }),
  });

  return {
    mlsMatches: query.data?.mlsMatches ?? [],
    totalMlsMatches: query.data?.totalMlsMatches ?? 0,
    hasNextMlsPage: !!query.hasNextPage,
    fetchNextMlsPage: query.fetchNextPage,
    isMatchesLoading: query.isLoading,
    isFetchingNextMlsPage: query.isFetchingNextPage,
  };
};

export default useWantAdMlsMatches;
